import type { AxiosInstance } from 'axios';
import type { IntegrationPartner } from '../integrations/contracts';
import type {
  CasePermissions,
  CommentHistoryCursor,
  CommentHistoryPage,
  GetCommentHistoryRequest,
  UpdateCasePermissionsRequest,
} from './contracts';
import type { GetCaseFeedByActionsRequest, CaseFeedByActionItem } from './contracts/case-feed-by-actions';
import type { ListCasesRequest, ListCasesResponse } from './contracts/list-cases';
import type {
  CaseTimeEntry,
  CreateTimeEntryRequest,
  DeleteTimeEntryRequest,
  ListTimeEntriesPage,
  ListTimeEntriesRequest,
  UpdateTimeEntryRequest,
} from './contracts/time-entry';
import type { CloneCaseRequest, ClonedCase } from './contracts/clone-case';

/** Request parameters for retrieving the webhooks scenario for a company. */
export interface GetWebhooksByCompanyRequest {
  companyId: string;
}

/** Request payload for creating a webhooks scenario. */
export interface CreateWebhookRequest {
  companyId: string;
  baseUrl: string;
}

/**
 * Gateway for interacting with the Cases API.
 */
export class CasesGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /** Retrieves the webhooks scenario for a company. */
  async getWebhooksByCompany(params: GetWebhooksByCompanyRequest): Promise<IntegrationPartner[]> {
    const { data } = await this.httpClient.get<IntegrationPartner[]>(`${this.baseUrl}/webhooks`, {
      params,
    });
    return data;
  }

  /** Creates a webhook. */
  async createWebhook(payload: CreateWebhookRequest): Promise<IntegrationPartner> {
    const { data } = await this.httpClient.post<IntegrationPartner>(`${this.baseUrl}/webhooks`, payload);
    return data;
  }

  /** Retrieves one chronological page of the edit/delete history of a case comment. */
  async getCommentHistory(params: GetCommentHistoryRequest): Promise<CommentHistoryPage> {
    const query: { limit?: number; cursor?: CommentHistoryCursor } = {};
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.cursor !== undefined) query.cursor = params.cursor;

    const { data } = await this.httpClient.get<CommentHistoryPage>(
      `${this.baseUrl}/comment/history/${params.commentId}`,
      { params: query },
    );
    return data;
  }

  /** Retrieves the Cases permission configuration for the current company. */
  async getCasePermissions(): Promise<CasePermissions> {
    const { data } = await this.httpClient.get<CasePermissions>(`${this.baseUrl}/case-settings/permissions`);
    return data;
  }

  /** Updates the Cases permission configuration for the current company. */
  async updateCasePermissions(payload: UpdateCasePermissionsRequest): Promise<CasePermissions> {
    const { data } = await this.httpClient.patch<CasePermissions>(
      `${this.baseUrl}/case-settings/permissions`,
      payload,
    );
    return data;
  }

  /**
   * Retrieves case feed entries for a batch of action IDs.
   * @param payload - The action type and list of action IDs to look up.
   * @returns Array of case feed items linking each action to a case.
   */
  async getCaseFeedByActions(payload: GetCaseFeedByActionsRequest): Promise<CaseFeedByActionItem[]> {
    const { data } = await this.httpClient.post<CaseFeedByActionItem[]>(
      `${this.baseUrl}/cases-feed/by-actions`,
      payload,
    );
    return data;
  }

  /** Lists cases with the given filters. `workGroupId` accepts multiple ids (union of members across groups) and is sent in the body to avoid GET URL length limits. */
  async listCases(payload: ListCasesRequest): Promise<ListCasesResponse> {
    const { data } = await this.httpClient.post<ListCasesResponse>(`${this.baseUrl}/cases/list`, payload);
    return data;
  }

  /** Clones a source case into a new one, server-side, optionally copying comments and/or attachments. */
  async cloneCase(payload: CloneCaseRequest): Promise<ClonedCase> {
    const { caseId, ...body } = payload;
    const { data } = await this.httpClient.post<ClonedCase>(`${this.baseUrl}/cases/${caseId}/clone`, body);
    return data;
  }

  /** Creates a time entry (apontamento) on a case. */
  async createTimeEntry(payload: CreateTimeEntryRequest): Promise<CaseTimeEntry> {
    const { caseId, ...body } = payload;
    const { data } = await this.httpClient.post<CaseTimeEntry>(`${this.baseUrl}/cases/${caseId}/time-entries`, body);
    return data;
  }

  /** Retrieves one page of time entries (apontamentos) for a case. */
  async listTimeEntries(params: ListTimeEntriesRequest): Promise<ListTimeEntriesPage> {
    const { caseId, ...query } = params;
    const { data } = await this.httpClient.get<ListTimeEntriesPage>(`${this.baseUrl}/cases/${caseId}/time-entries`, {
      params: query,
    });
    return data;
  }

  /** Updates a time entry (apontamento) on a case. */
  async updateTimeEntry(payload: UpdateTimeEntryRequest): Promise<CaseTimeEntry> {
    const { caseId, timeEntryId, ...body } = payload;
    const { data } = await this.httpClient.patch<CaseTimeEntry>(
      `${this.baseUrl}/cases/${caseId}/time-entries/${timeEntryId}`,
      body,
    );
    return data;
  }

  /** Deletes (soft-delete) a time entry (apontamento) on a case. */
  async deleteTimeEntry(params: DeleteTimeEntryRequest): Promise<CaseTimeEntry> {
    const { caseId, timeEntryId } = params;
    const { data } = await this.httpClient.delete<CaseTimeEntry>(
      `${this.baseUrl}/cases/${caseId}/time-entries/${timeEntryId}`,
    );
    return data;
  }
}

export type {
  CommentHistoryEntry,
  CommentHistoryChangeType,
  CommentHistoryCursor,
  CommentHistoryPage,
  GetCommentHistoryRequest,
} from './contracts/comment-history';
export type { CasePermissions, CasePermissionsValue, UpdateCasePermissionsRequest } from './contracts/case-permissions';
export type { GetCaseFeedByActionsRequest, CaseFeedByActionItem } from './contracts/case-feed-by-actions';
export type { CaseListItem, ListCasesCursor, ListCasesRequest, ListCasesResponse } from './contracts/list-cases';
export type {
  CaseTimeEntry,
  CreateTimeEntryRequest,
  DeleteTimeEntryRequest,
  ListTimeEntriesPage,
  ListTimeEntriesRequest,
  TimeEntryCursor,
  UpdateTimeEntryRequest,
} from './contracts/time-entry';
export type { CloneCaseRequest, ClonedCase } from './contracts/clone-case';
